/** The primary context provider for managing task data, tree structures, and global task-related state. */
// @ts-check
import { useMemo, useState, useEffect } from 'react';
import useTaskSubscription from '@/features/tasks/api/use-task-subscription.js';
import { useAuth } from '@/features/auth/useAuth';
import { useFilterContext } from '@/features/tasks/hooks/list/use-list-filters.js';
import {
  getTaskProjects,
  getTaskContexts,
} from '@/features/tasks/utils/data/taskTags';
import {
  getAllDescendantIds,
  applyFilters,
  applyActiveFilters,
  buildActiveMap,
  processSnapshotToMap,
  processProjectSnapshot,
} from '@/features/tasks/context/tasksContextUtils';
import { TasksContext } from '@/features/tasks/context/TasksContext.def';
import {
  subscribeToProjects,
  subscribeToContexts,
  backfillMissingProjectColors,
} from '@/services/tagService';

// ─────────────────────────────────────────────────────────────────────────────
// Provider Component
// ─────────────────────────────────────────────────────────────────────────────
/** @param {{ children: React.ReactNode; initialTasksOverride?: Task[]; initialTaskTreeOverride?: any[] }} props */
export function TasksProvider({
  children,
  initialTasksOverride,
  initialTaskTreeOverride,
}) {
  const { user } = useAuth();
  // Destructure the taskTree and duplicateOrderKeyTaskIds from useTaskSubscription
  const subscription = useTaskSubscription();
  const allTasks =
    initialTasksOverride !== undefined
      ? initialTasksOverride
      : subscription.allTasks;
  const taskTree =
    initialTaskTreeOverride !== undefined
      ? initialTaskTreeOverride
      : subscription.taskTree;
  const loading =
    initialTasksOverride !== undefined ? false : subscription.loading;
  const error = initialTasksOverride !== undefined ? null : subscription.error;
  const duplicateOrderKeyTaskIds =
    initialTasksOverride !== undefined
      ? new Set()
      : subscription.duplicateOrderKeyTaskIds;
  const { hiddenProjects, hiddenContexts } = useFilterContext();

  const [masterProjectMap, setMasterProjectMap] = useState(new Map());
  const [masterProjectColorMap, setMasterProjectColorMap] = useState(
    () => new Map(),
  );
  const [projectIdsMissingColor, setProjectIdsMissingColor] = useState([]);
  const [masterContextMap, setMasterContextMap] = useState(new Map());
  const [projectFilter, setProjectFilter] = useState([]);
  const [contextFilter, setContextFilter] = useState([]);

  const tasksMap = useMemo(() => {
    if (!allTasks) return new Map();
    return new Map(allTasks.map((task) => [task.id, task]));
  }, [allTasks]);

  // Set of task IDs that have at least one child task.
  // Parent tasks should not contribute their own duration to summations.
  const parentTaskIds = useMemo(() => {
    const ids = new Set();
    if (allTasks)
      allTasks.forEach((t) => {
        if (t.parentId) ids.add(t.parentId);
      });
    return ids;
  }, [allTasks]);

  useEffect(() => {
    if (!user) {
      setMasterProjectMap(new Map());
      setMasterProjectColorMap(new Map());
      setProjectIdsMissingColor([]);
      setMasterContextMap(new Map());
      return;
    }

    const unsubscribeProjects = subscribeToProjects(
      user.uid,
      (snapshot) => {
        const { displayMap, colorMap, missingColorIds } =
          processProjectSnapshot(snapshot);
        setMasterProjectMap(displayMap);
        setMasterProjectColorMap(colorMap);
        setProjectIdsMissingColor(missingColorIds);
      },
      () => {
        setMasterProjectMap(new Map());
        setMasterProjectColorMap(new Map());
        setProjectIdsMissingColor([]);
      },
    );

    const unsubscribeContexts = subscribeToContexts(
      user.uid,
      (snapshot) => {
        setMasterContextMap(processSnapshotToMap(snapshot));
      },
      () => {
        setMasterContextMap(new Map());
      },
    );

    return () => {
      unsubscribeProjects();
      unsubscribeContexts();
    };
  }, [user]);

  const projectIdsMissingColorKey = useMemo(
    () => projectIdsMissingColor.join('|'),
    [projectIdsMissingColor],
  );

  useEffect(() => {
    if (!user?.uid || projectIdsMissingColor.length === 0) return;
    let cancelled = false;
    backfillMissingProjectColors(
      user.uid,
      projectIdsMissingColor,
      masterProjectColorMap,
    ).catch((err) => {
      if (!cancelled) {
        console.error('TagService: project color backfill failed:', err);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [user?.uid, projectIdsMissingColor, projectIdsMissingColorKey, masterProjectColorMap]);

  /**
   * Map of active (non-completed) project IDs → display names.
   *
   * Dual-format normalization — both formats are merged per task:
   * - `task.projects` (string[]) — current format
   * - `task.project`  (string | string[]) — legacy backward-compat; tasks
   *   created before the projects-array migration may still carry this field.
   *
   * Only projects that appear on at least one active task are included, so the
   * map reflects what is actually in use and can be shown in filter UIs.
   */
  const activeProjectMap = useMemo(() => {
    return buildActiveMap(allTasks, masterProjectMap, getTaskProjects);
  }, [allTasks, masterProjectMap]);

  /**
   * Map of active (non-completed) context IDs → display names.
   *
   * Same dual-format normalization as `activeProjectMap`:
   * - `task.contexts` (string[]) — current format
   * - `task.context`  (string | string[]) — legacy backward-compat field.
   */
  const activeContextMap = useMemo(() => {
    return buildActiveMap(allTasks, masterContextMap, getTaskContexts);
  }, [allTasks, masterContextMap]);

  const personDisplayMap = useMemo(() => {
    if (!allTasks) return new Map();
    const uncompletedTasks = allTasks.filter((task) => !task.completed);
    /** @type {Map<string, string>} */
    const newMap = new Map();
    uncompletedTasks.forEach((task) => {
      if (task.person) {
        const personName = task.person.trim();
        if (personName) {
          newMap.set(personName, personName);
        }
      }
    });
    return newMap;
  }, [allTasks]);

  /**
   * Returns a filtered list of allTasks based on the globally hidden projects and contexts.
   *
   * Compatibility Note:
   * It processes both modern array-based tags (`task.projects`, `task.contexts`)
   * and legacy string/array tags (`task.project`, `task.context`) to ensure
   * older tasks are filtered correctly.
   *
   * Filtering Branches:
   * 1. Both Project & Context filters active: Hides the task if it contains ANY hidden project OR ANY hidden context.
   * 2. Only Project or Only Context filter active: Hides the task if it contains ANY hidden project (or context).
   */
  const filteredTasks = useMemo(() => {
    const hiddenFiltered = applyFilters(
      allTasks,
      hiddenProjects,
      hiddenContexts,
    );
    return applyActiveFilters(hiddenFiltered, projectFilter, contextFilter);
  }, [allTasks, hiddenProjects, hiddenContexts, projectFilter, contextFilter]);

  /** @type {TasksContextValue} */
  const value = {
    allTasks,
    tasksMap,
    taskTree, // Provide the taskTree in the context
    filteredTasks,
    projectFilter,
    setProjectFilter,
    contextFilter,
    setContextFilter,
    loading,
    error,
    projectDisplayMap: activeProjectMap,
    projectColorMap: masterProjectColorMap,
    allProjectMap: masterProjectMap,
    contextDisplayMap: activeContextMap,
    personDisplayMap,
    user,
    parentTaskIds,
    duplicateOrderKeyTaskIds, // Add the set of conflicting IDs to the context
    getAllDescendantIds,
  };

  return (
    <TasksContext.Provider value={value}>{children}</TasksContext.Provider>
  );
}
